import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionContent,
  AccordionHeader,
  AccordionItem,
  AccordionTrigger,
  Chip,
  cn,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@c2fo/liquidity';
import { CombinedPricingSuggestion } from '@/enterprise/data/usePricingSuggestion';
import RateDisplay from '@/enterprise/features/homepage/marketTable/RateDisplay';
import { useOfferDiscountAPRDisplay } from '@/enterprise/features/takerMarketTable/utils/useOfferDiscountAPRDisplay';
import useDataDog from '@/reporting/DataDog';
import getTakerMarketDivisionTitle from '@/utils/getTakerMarketDivisionTitle';
import getTakerMarketName from '@/utils/getTakerMarketName';
import useLocaleFormat from '@/utils/useLocaleFormat';
import usePricingTypeDisplay from '@/utils/usePricingTypeDisplay';
import BlendedRateDisplay from '../BlendedRateDisplay';
import { RateType, useOfferBuilderContext } from '../OfferBuilderContext';
import useStableGroupByBuyer from '../useStableGroupByBuyer';
import {
  buildPricingOptionsForMarket,
  buildTakerMarketKey,
  getNameYourRateMarkets,
  getTakerMarketErrors,
  useRateTypeOptions,
  hasAnyTakerMarketError,
} from '../utils';
import ApplyToAllOptions from './ApplyToAll/ApplyToAllOptions';
import LearnMoreAboutRateOptionsModal from './LearnMoreAboutRateOptionsModal';
import PricingStrategyRadioGroup from './PricingStrategyRadioGroup';
import RecurringDiscountConfirmModal from './RecurringDiscountConfirmModal';

interface PricingStrategyOptionsBaseProps {
  pricingData: CombinedPricingSuggestion | null;
  onRateTypeChange: (value: RateType) => void;
}

const PricingStrategyOptionsBase = ({ pricingData, onRateTypeChange }: PricingStrategyOptionsBaseProps) => {
  const { t } = useTranslation();
  const { asNumber, asCurrency } = useLocaleFormat();
  const { getPricingTypeDisplay } = usePricingTypeDisplay();
  const { dispatch, state } = useOfferBuilderContext();
  const {
    frequency,
    rateType,
    selected,
    takerMarketErrors,
    workingTakerMarkets,
    preferredRateSelected,
    recurringDiscountModalShown,
  } = state;
  const rateOptions = useRateTypeOptions();
  const buyers = useStableGroupByBuyer({ takerMarkets: selected });
  const nameYourRateMarkets = getNameYourRateMarkets(selected);
  const isAlreadyDiscount = pricingData?.overallOptimalRateType === 'DISC';

  const shouldShowRecurringDiscountModal =
    frequency === 'recurring' && rateType === 'DISC' && nameYourRateMarkets.length > 0 && pricingData !== null;

  const [modalOpen, setModalOpen] = useState(false);
  const [recurringDiscountModalOpen, setRecurringDiscountModalOpen] = useState(false);

  useEffect(() => {
    if (shouldShowRecurringDiscountModal && !recurringDiscountModalShown) {
      dispatch({ type: 'SET_RECURRING_DISCOUNT_MODAL_SHOWN' });
      setRecurringDiscountModalOpen(true);
    }
  }, [shouldShowRecurringDiscountModal, recurringDiscountModalShown, dispatch]);
  const [manualOpenBuyers, setManualOpenBuyers] = useState<string[]>(() => buyers.map((b) => b.makerOrganizationUuid));

  const openBuyers = preferredRateSelected ? [] : manualOpenBuyers;
  const { offerDiscountAPRDisplay } = useOfferDiscountAPRDisplay();
  const effectiveRateType = rateType ?? 'APR'; // fallback to APR
  const rateTypeLabel = effectiveRateType === 'APR' ? t('core.apr') : t('core.discountAbbreviation');
  const hasError = hasAnyTakerMarketError(state, 'NO_RATE_SELECTED');
  const { addError } = useDataDog();
  const hasLoggedEmptyRatesRef = useRef(false);

  // Only flag markets that are eligible and have a matching pricing entry —
  // ineligible markets (eligibleInvoiceAmount <= 0) and missing entries legitimately produce no options.
  const marketsWithEmptyRates = useMemo(() => {
    if (!pricingData || !frequency) {
      return [];
    }

    return selected.filter((takerMarket) => {
      if (takerMarket.eligibleInvoiceAmount <= 0) {
        return false;
      }

      const pricingInformation = pricingData.supplierMarkets.find(
        (tm) => tm.marketUuid === takerMarket.marketUuid && tm.supplierDivisionUuid === takerMarket.takerDivisionUuid
      );
      if (!pricingInformation) {
        return false;
      }

      const rateKey = effectiveRateType === 'DISC' ? 'discount' : 'apr';
      const trendingRate = pricingInformation[rateKey][frequency]?.trendingRate;
      return !Number.isFinite(trendingRate);
    });
  }, [pricingData, selected, effectiveRateType, frequency]);

  useEffect(() => {
    if (hasLoggedEmptyRatesRef.current || marketsWithEmptyRates.length === 0) {
      return;
    }

    hasLoggedEmptyRatesRef.current = true;
    marketsWithEmptyRates.forEach((takerMarket) => {
      addError('Pricing Suggestions - Empty Rates', {
        marketUuid: takerMarket.marketUuid,
        supplierDivisionUuid: takerMarket.takerDivisionUuid,
      });
    });
  }, [marketsWithEmptyRates, addError]);

  const handleRateTypeChange = (value: RateType) => {
    if (
      value === 'DISC' &&
      frequency === 'recurring' &&
      nameYourRateMarkets.length > 0 &&
      pricingData !== null &&
      !recurringDiscountModalShown
    ) {
      dispatch({ type: 'SET_RECURRING_DISCOUNT_MODAL_SHOWN' });
      setRecurringDiscountModalOpen(true);
      return;
    }

    onRateTypeChange(value);
  };

  const handleConfirmDiscount = () => {
    setRecurringDiscountModalOpen(false);
    onRateTypeChange('DISC');
  };

  const handleSwitchToApr = () => {
    setRecurringDiscountModalOpen(false);
    onRateTypeChange('APR');
  };

  return (
    <>
      <LearnMoreAboutRateOptionsModal open={modalOpen} onClose={() => setModalOpen(false)} />
      <RecurringDiscountConfirmModal
        open={recurringDiscountModalOpen}
        isAlreadyDiscount={isAlreadyDiscount}
        onConfirmDiscount={handleConfirmDiscount}
        onSwitchToApr={handleSwitchToApr}
        onClose={() => setRecurringDiscountModalOpen(false)}
      />
      <div>
        <div className="mb-2 flex flex-col justify-between gap-2 sm:flex-row sm:items-center sm:gap-4">
          <div>
            <div className={cn('text-lg font-medium', { 'text-red-700': hasError })}>{t('builder.selectYourRate')}</div>
            <button
              className="mt-0.5 text-left text-sm font-medium text-blue-500"
              onClick={() => setModalOpen(true)}
              type="button"
            >
              {t('builder.learnMoreAboutRateOptions')}
            </button>
          </div>
          <div className="flex flex-col items-end">
            <Select onValueChange={handleRateTypeChange} value={effectiveRateType}>
              <SelectTrigger aria-label={t('core.rateType')} className="w-full md:w-fit" size="sm">
                <span className="flex items-center gap-1">
                  <span className="text-text-secondary">{t('core.rateType')}:</span>
                  <span className="font-medium">
                    <SelectValue />
                  </span>
                </span>
              </SelectTrigger>
              <SelectContent align="end" className="min-w-full md:min-w-80">
                {rateOptions.map((rateOption) => (
                  <SelectItem key={rateOption.value} value={rateOption.value}>
                    <p>{rateOption.label}</p>
                    <p className="block text-wrap text-sm [button_&]:hidden">{rateOption.desc}</p>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {nameYourRateMarkets.length > 1 && (
          <ApplyToAllOptions
            takerMarkets={nameYourRateMarkets}
            pricingData={pricingData ?? undefined}
            selectedRateType={effectiveRateType}
          />
        )}
        <Accordion className="space-y-4" onValueChange={setManualOpenBuyers} type="multiple" value={openBuyers}>
          {buyers.map((buyer) => {
            const buyerErrors = buyer.takerMarkets.reduce((count, takerMarket) => {
              const marketKey = buildTakerMarketKey(takerMarket);
              const errors = takerMarketErrors[marketKey] || [];
              return count + errors.length;
            }, 0);
            const hasBuyerError = buyerErrors > 0;

            // Calculate rate selection counts for this buyer
            const total = buyer.takerMarkets.length;
            const selectedCount = buyer.takerMarkets.filter((tm) => {
              const marketKey = buildTakerMarketKey(tm);
              const market = workingTakerMarkets[marketKey];

              // For price discovery markets, check if a rate is selected
              if (tm.marketPricingType === 'PRICE_DISCOVERY') {
                return market?.rate !== undefined && market?.rate !== null;
              }

              // For fixed rate markets (BENCHMARK or STATIC), they always have a rate
              return tm.marketPricingType === 'BENCHMARK' || tm.marketPricingType === 'STATIC';
            }).length;

            return (
              <AccordionItem
                key={buyer.makerOrganizationUuid}
                className="rounded-xl border-[0.5px] border-stroke bg-white"
                value={buyer.makerOrganizationUuid}
              >
                <AccordionHeader asChild className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="flex grow flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <div className="font-medium">{buyer.makerOrganizationName}</div>
                        <Chip
                          className={cn('mt-0.5 bg-gray-100 text-gray-900', {
                            'bg-red-600 text-white': hasBuyerError,
                          })}
                          isRound
                          label={
                            hasBuyerError
                              ? t('builder.divisionNeedsRate', { count: buyerErrors })
                              : t('builder.ratesSelected', { selected: selectedCount, total, count: total })
                          }
                          size="xs"
                          type="default"
                          variant="filled"
                        />
                      </div>
                      <div className="sm:text-right">
                        <div className="text-lg font-bold">
                          <BlendedRateDisplay takerMarkets={buyer.takerMarkets} />
                        </div>
                        <div className="text-sm text-text-secondary">{t('core.blendedRate')}</div>
                      </div>
                    </div>
                    <AccordionTrigger className="ml-0" name="Expand buyer" />
                  </div>
                </AccordionHeader>
                {buyer.takerMarkets.map((takerMarket) => {
                  const marketKey = buildTakerMarketKey(takerMarket);
                  const marketRate = workingTakerMarkets[marketKey];
                  const marketErrors = getTakerMarketErrors(state, marketKey);
                  const hasTakerMarketError = marketErrors.length > 0;

                  const options = buildPricingOptionsForMarket({
                    pricingData,
                    takerMarket,
                    rateType: effectiveRateType,
                    frequency,
                  }).map((option) => ({
                    pricingRateUuid: option.pricingRateUuid,
                    rate: option.rate,
                    rateType: option.rateType,
                    suggestRateType: pricingData?.overallOptimalRateType,
                    suggestedRate: option.rate,
                    suggestedRateEnd: option.suggestedRateEnd,
                    suggestedRateStart: option.suggestedRateStart,
                    type: option.type,
                  }));

                  return (
                    <AccordionContent key={takerMarket.id} className="px-4">
                      <div className="overflow-hidden rounded-xl border-[0.5px] border-stroke bg-white">
                        <div className="flex flex-col gap-1 bg-canvas p-4 sm:flex-row sm:justify-between sm:gap-4">
                          <div className="truncate">
                            <div className="text-sm font-medium">{getTakerMarketName(takerMarket)}</div>
                            <div
                              className="truncate text-sm font-medium"
                              title={getTakerMarketDivisionTitle(takerMarket)?.title}
                            >
                              {getTakerMarketDivisionTitle(takerMarket)?.content}
                            </div>
                            <div className="flex items-center gap-1 text-sm text-text-secondary">
                              <div>{getPricingTypeDisplay(takerMarket)}</div>
                              <div className="h-[3px] w-[3px] rounded-full bg-text-secondary" />
                              <div>{takerMarket.currency}</div>
                            </div>
                          </div>
                          <div className="sm:text-right">
                            <div className="text-lg font-bold uppercase">
                              {takerMarket.marketPricingType === 'PRICE_DISCOVERY' && marketRate.rate ? (
                                offerDiscountAPRDisplay(marketRate.rate, rateTypeLabel)
                              ) : takerMarket.marketPricingType === 'BENCHMARK' ||
                                takerMarket.marketPricingType === 'STATIC' ? (
                                <RateDisplay showInfo={false} showDiscountAmount={false} takerMarket={takerMarket} />
                              ) : (
                                '--'
                              )}
                            </div>
                            <div className="text-sm text-text-secondary">
                              {asCurrency(takerMarket.eligibleInvoiceAmount, takerMarket.currency)}
                            </div>
                            <div className="text-sm text-text-secondary">
                              {`${asNumber({ value: takerMarket.eligibleDpeWeightedAvg })} ${t('core.daysPaidEarly')}`}
                            </div>
                          </div>
                        </div>
                        <div className="p-4">
                          <PricingStrategyRadioGroup
                            hasError={hasTakerMarketError}
                            marketPricingType={takerMarket.marketPricingType}
                            options={options}
                            selectedRateType={effectiveRateType}
                            takerMarket={takerMarket}
                          />
                        </div>
                      </div>
                    </AccordionContent>
                  );
                })}
              </AccordionItem>
            );
          })}
        </Accordion>
      </div>
    </>
  );
};

export default PricingStrategyOptionsBase;
